import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "ma_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const tok = getToken();
  if (tok) config.headers.Authorization = `Bearer ${tok}`;
  return config;
});

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export async function submitQuote(payload) {
  const { data } = await api.post("/quotes", payload);
  return data;
}

export async function submitContact(payload) {
  const { data } = await api.post("/contact", payload);
  return data;
}

// ====== Auth ======
export async function authLogin(email, password) {
  const { data } = await api.post("/auth/login", { email, password });
  setToken(data.token);
  return data.user;
}

export async function authRegister(payload) {
  const { data } = await api.post("/auth/register", payload);
  setToken(data.token);
  return data.user;
}

export async function authMe() {
  const { data } = await api.get("/auth/me");
  return data;
}

export function authLogout() {
  setToken(null);
}

export async function authUpdateProfile(payload) {
  const { data } = await api.patch("/auth/me", payload);
  return data;
}

export async function authChangePassword(current_password, new_password) {
  const { data } = await api.post("/auth/change-password", { current_password, new_password });
  return data;
}

// ====== Espace client ======
export const meApi = {
  summary: () => api.get("/me/summary").then((r) => r.data),
  echeances: () => api.get("/me/echeances").then((r) => r.data),
  getPlaylist: () => api.get("/me/playlist").then((r) => r.data),
  savePlaylist: (b) => api.put("/me/playlist", b).then((r) => r.data),
  playlistPdfUrl: () => `${api.defaults.baseURL}/me/playlist/pdf`,
  playlistM3uUrl: () => `${api.defaults.baseURL}/me/playlist/m3u`,
  getQuestionnaire: () => api.get("/me/questionnaire").then((r) => r.data),
  saveQuestionnaire: (b) => api.put("/me/questionnaire", b).then((r) => r.data),
  getQuestionnairePrep: () => api.get("/me/questionnaire-prep").then((r) => r.data),
  saveQuestionnairePrep: (b) => api.put("/me/questionnaire-prep", b).then((r) => r.data),
  getPhotobooth: () => api.get("/me/photobooth").then((r) => r.data),
  savePhotobooth: (b) => api.put("/me/photobooth", b).then((r) => r.data),
  getPlanning: () => api.get("/me/planning").then((r) => r.data),
  savePlanning: (b) => api.put("/me/planning", b).then((r) => r.data),
  planningPdfUrl: () => `${api.defaults.baseURL}/me/planning/pdf`,
  rdv: () => api.get("/me/rdv").then((r) => r.data),
  rdvIcsUrl: (id) => `${api.defaults.baseURL}/me/rdv/${id}/ics`,
  requestRdv: (body) => api.post("/me/rdv/request", body).then((r) => r.data),
  commandes: () => api.get("/me/commandes").then((r) => r.data),
  finaliserCommande: (id) => api.patch(`/me/commandes/${id}/finaliser`).then((r) => r.data),
  contrat: () => api.get("/me/contrat").then((r) => r.data),
  validations: () => api.get("/me/validations").then((r) => r.data),
  pendingActions: () => api.get("/me/pending-actions").then((r) => r.data),
  validationFileUrl: (id) => `${api.defaults.baseURL}/me/validations/${id}/file`,
  submitValidation: (id, body) => api.post(`/me/validations/${id}/decision`, body).then((r) => r.data),
  // In-app notifications (bell)
  notifications: (limit = 50) => api.get("/me/notifications", { params: { limit } }).then((r) => r.data),
  notificationsUnread: () => api.get("/me/notifications/unread-count").then((r) => r.data),
  notificationsMarkRead: (ids = null) => api.post("/me/notifications/mark-read", ids ? { ids } : {}).then((r) => r.data),
  notificationsDelete: (id) => api.delete(`/me/notifications/${id}`).then((r) => r.data),
};

// ====== Admin ======
export const adminApi = {
  stats: () => api.get("/admin/stats").then((r) => r.data),
  timeline: (months = 12) => api.get("/admin/stats/timeline", { params: { months } }).then((r) => r.data),
  breakdown: () => api.get("/admin/stats/breakdown").then((r) => r.data),
  funnel: () => api.get("/admin/stats/funnel").then((r) => r.data),
  revenue: (months = 12) => api.get("/admin/stats/revenue", { params: { months } }).then((r) => r.data),
  users: () => api.get("/admin/users").then((r) => r.data),
  userFull: (id) => api.get(`/admin/users/${id}/full`).then((r) => r.data),
  updateUser: (id, payload) => api.patch(`/admin/users/${id}`, payload).then((r) => r.data),
  createUser: (b) => api.post("/admin/users", b).then((r) => r.data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`).then((r) => r.data),
  resetUserPassword: (id) => api.post(`/admin/users/${id}/reset-password`).then((r) => r.data),
  resetUserPlaylist: (id) => api.delete(`/admin/users/${id}/playlist`).then((r) => r.data),
  createRdv: (b) => api.post("/admin/rdv", b).then((r) => r.data),
  deleteRdv: (id) => api.delete(`/admin/rdv/${id}`).then((r) => r.data),
  rdvIcsUrl: (id) => `${api.defaults.baseURL}/admin/rdv/${id}/ics`,
  // Weekly recap
  recapPreviewUrl: () => `${api.defaults.baseURL}/admin/recap/preview`,
  recapSendNow: () => api.post("/admin/recap/send-now").then((r) => r.data),
  // Client validations (photobooth template / audio maquette)
  validationsList: (userId) => api.get("/admin/validations", { params: userId ? { user_id: userId } : {} }).then((r) => r.data),
  validationFileUrl: (id) => `${api.defaults.baseURL}/admin/validations/${id}/file`,
  validationDelete: (id) => api.delete(`/admin/validations/${id}`).then((r) => r.data),
  validationUpload: async ({ user_id, kind, label, file }) => {
    const fd = new FormData();
    fd.append("user_id", user_id);
    fd.append("kind", kind);
    fd.append("label", label || "");
    fd.append("file", file);
    const { data } = await api.post("/admin/validations/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  upsertContrat: (b) => api.put("/admin/contrat", b).then((r) => r.data),
  listUnavailable: () => api.get("/admin/calendar/unavailable").then((r) => r.data),
  addUnavailable: (b) => api.post("/admin/calendar/unavailable", b).then((r) => r.data),
  removeUnavailable: (date) => api.delete(`/admin/calendar/unavailable/${date}`).then((r) => r.data),
  calendarEvents: (month) => api.get(`/admin/calendar/events?month=${month}`).then((r) => r.data),
  calendarPdfUrl: (month) => `${API}/admin/calendar/pdf?month=${month}`,
  livreRecettes: (year) => api.get(`/admin/accounting/livre-recettes?year=${year}`).then((r) => r.data),
  adminOnlyMargins: (year) => api.get(`/admin/accounting/margins?year=${year}`).then((r) => r.data),
  livreRecettesPdfUrl: (year) => `${API}/admin/accounting/livre-recettes/pdf?year=${year}`,
  // Blog
  listBlogPosts: () => api.get("/admin/blog/posts").then((r) => r.data),
  createBlogPost: (b) => api.post("/admin/blog/posts", b).then((r) => r.data),
  updateBlogPost: (id, b) => api.put(`/admin/blog/posts/${id}`, b).then((r) => r.data),
  deleteBlogPost: (id) => api.delete(`/admin/blog/posts/${id}`).then((r) => r.data),
  // Reviews — admin
  listPendingReminders: () => api.get("/admin/reviews/pending-reminders").then((r) => r.data),
  triggerReviewReminders: () => api.post("/admin/reviews/send-reminders").then((r) => r.data),
  pendingReviewsCount: () => api.get("/admin/reviews/pending-count").then((r) => r.data),
  // Billing — devis & factures
  listBilling: (params = {}) => api.get("/admin/billing", { params }).then((r) => r.data),
  billingDefaultTerms: () => api.get("/admin/billing/default-terms").then((r) => r.data),
  getBilling: (id) => api.get(`/admin/billing/${id}`).then((r) => r.data),
  createBilling: (b) => api.post("/admin/billing", b).then((r) => r.data),
  previewBilling: (b) => api.post("/admin/billing/preview", b, { responseType: "blob" }).then((r) => r.data),
  updateBilling: (id, b) => api.put(`/admin/billing/${id}`, b).then((r) => r.data),
  deleteBilling: (id) => api.delete(`/admin/billing/${id}`).then((r) => r.data),
  convertBilling: (id) => api.post(`/admin/billing/${id}/convert-to-invoice`).then((r) => r.data),
  markAcompteReceived: (id, body = {}) => api.post(`/admin/billing/${id}/mark-acompte-received`, body).then((r) => r.data),
  generateBalanceInvoice: (id, body = {}) => api.post(`/admin/billing/${id}/generate-balance-invoice`, body).then((r) => r.data),
  billingHealthCheck: () => api.get(`/admin/billing/health-check`).then((r) => r.data),
  markSigned: (id, body = {}) => api.post(`/admin/billing/${id}/mark-signed`, body).then((r) => r.data),
  setPaidAt: (id, body) => api.post(`/admin/billing/${id}/set-paid-at`, body).then((r) => r.data),
  requestReview: (body) => api.post("/admin/reviews/request", body).then((r) => r.data),
  // Prestataires directory
  listPrestataires: () => api.get("/admin/prestataires").then((r) => r.data),
  createPrestataire: (b) => api.post("/admin/prestataires", b).then((r) => r.data),
  updatePrestataire: (id, b) => api.patch(`/admin/prestataires/${id}`, b).then((r) => r.data),
  deletePrestataire: (id) => api.delete(`/admin/prestataires/${id}`).then((r) => r.data),
  // Dossier assignments
  listDossierPrestataires: (uid) => api.get(`/admin/dossiers/${uid}/prestataires`).then((r) => r.data),
  assignPrestataire: (uid, b) => api.post(`/admin/dossiers/${uid}/prestataires`, b).then((r) => r.data),
  updateAssignment: (aid, b) => api.patch(`/admin/dossier-prestataires/${aid}`, b).then((r) => r.data),
  removeAssignment: (aid) => api.delete(`/admin/dossier-prestataires/${aid}`).then((r) => r.data),
  sendBilling: (id) => api.post(`/admin/billing/${id}/send`).then((r) => r.data),
  billingPdfUrl: (id) => `${api.defaults.baseURL}/admin/billing/${id}/pdf`,
  createBillingFromQuote: (quoteId) => api.post(`/admin/billing/from-quote/${quoteId}`).then((r) => r.data),
};

// ====== Public signature ======
export const signApi = {
  get: (token) => api.get(`/billing/sign/${token}`).then((r) => r.data),
  submit: (token, body) => api.post(`/billing/sign/${token}`, body).then((r) => r.data),
  refuse: (token, body) => api.post(`/billing/sign/${token}/refuse`, body).then((r) => r.data),
  pdfUrl: (token) => `${api.defaults.baseURL}/billing/sign/${token}/pdf`,
};

// ====== Client documents (espace client) ======
export const documentsApi = {
  list: () => api.get("/me/documents").then((r) => r.data),
  pdfUrl: (id) => `${api.defaults.baseURL}/me/documents/${id}/pdf`,
  createDepositCheckout: (id, origin_url) =>
    api.post(`/me/documents/${id}/deposit-checkout`, { origin_url }).then((r) => r.data),
  requestDepositTransfer: (id) =>
    api.post(`/me/documents/${id}/deposit-transfer-request`).then((r) => r.data),
  depositStatus: (sid) => api.get(`/me/documents/deposit-status/${sid}`).then((r) => r.data),
};

// ====== Brochure PDF (lead magnet) ======
export const brochureApi = {
  pdfUrl: () => `${api.defaults.baseURL}/brochure/pdf`,
  sendByEmail: (body) => api.post("/brochure/send", body).then((r) => r.data),
};

// ====== Catalogue produits (admin) ======
export const catalogApi = {
  list: (params = {}) => api.get("/admin/catalog", { params }).then((r) => r.data),
  create: (b) => api.post("/admin/catalog", b).then((r) => r.data),
  update: (id, b) => api.put(`/admin/catalog/${id}`, b).then((r) => r.data),
  del: (id) => api.delete(`/admin/catalog/${id}`).then((r) => r.data),
};

// ====== Comptabilité (admin) ======
export const accountingApi = {
  summary: (year) => api.get("/admin/accounting/summary", { params: { year } }).then((r) => r.data),
  getSettings: () => api.get("/admin/accounting/settings").then((r) => r.data),
  updateSettings: (b) => api.put("/admin/accounting/settings", b).then((r) => r.data),
};

// ====== Billetterie ======
export const billetterieApi = {
  // Public
  publicList: () => api.get("/billetterie/events").then((r) => r.data),
  publicGet: (slug) => api.get(`/billetterie/events/${slug}`).then((r) => r.data),
  createOrder: (b) => api.post("/billetterie/orders", b).then((r) => r.data),
  checkoutStatus: (sid) => api.get(`/billetterie/checkout/status/${sid}`).then((r) => r.data),
  publicTicket: (code) => api.get(`/billet/${code}`).then((r) => r.data),
  // Admin
  adminList: () => api.get("/admin/billetterie/events").then((r) => r.data),
  adminGet: (id) => api.get(`/admin/billetterie/events/${id}`).then((r) => r.data),
  adminCreate: (b) => api.post("/admin/billetterie/events", b).then((r) => r.data),
  adminUpdate: (id, b) => api.put(`/admin/billetterie/events/${id}`, b).then((r) => r.data),
  adminDelete: (id) => api.delete(`/admin/billetterie/events/${id}`).then((r) => r.data),
  adminTickets: (id) => api.get(`/admin/billetterie/events/${id}/tickets`).then((r) => r.data),
  adminCreateManualTickets: (b) => api.post("/admin/billetterie/tickets/manual", b).then((r) => r.data),
  adminScan: (code) => api.post("/admin/billetterie/scan", { code }).then((r) => r.data),
  // Billetterie
  adminScanStats: (id) => api.get(`/admin/billetterie/events/${id}/scan-stats`).then((r) => r.data),
  adminScansLog: (id) => api.get(`/admin/billetterie/events/${id}/scans`).then((r) => r.data),
  adminUploadImage: async (file) => {
    // Use native fetch to avoid axios instance's default Content-Type
    // interfering with multipart boundary generation.
    const fd = new FormData();
    fd.append("file", file);
    const token = getToken();
    const res = await fetch(`${API}/admin/billetterie/upload-image`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    if (!res.ok) {
      let detail = "Échec de l'envoi de l'image.";
      try { const j = await res.json(); if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail); } catch (_) {}
      const err = new Error(detail);
      err.response = { data: { detail }, status: res.status };
      throw err;
    }
    return res.json();
  },
};

// ====== Public blog ======
export const blogApi = {
  list: (params = {}) => api.get("/blog/posts", { params }).then((r) => r.data),
  get: (slug) => api.get(`/blog/posts/${slug}`).then((r) => r.data),
};

// ====== Reviews ======
export const reviewsApi = {
  // Public
  publicList: (limit = 60) => api.get("/reviews", { params: { limit } }).then((r) => r.data),
  publicStats: () => api.get("/reviews/stats").then((r) => r.data),
  // Logged-in client
  mySubmit: (b) => api.post("/me/reviews", b).then((r) => r.data),
  myList: () => api.get("/me/reviews").then((r) => r.data),
  // Admin
  adminList: (status) => api.get("/admin/reviews", { params: status ? { status } : {} }).then((r) => r.data),
  adminCreate: (b) => api.post("/admin/reviews", b).then((r) => r.data),
  adminUpdate: (id, b) => api.put(`/admin/reviews/${id}`, b).then((r) => r.data),
  adminDelete: (id) => api.delete(`/admin/reviews/${id}`).then((r) => r.data),
};

// ====== Public catalog (service pages) ======
export const publicCatalogApi = {
  byCategory: (category, include_options = true) =>
    api.get("/catalog/public", { params: { category, include_options } }).then((r) => r.data),
  list: ({ include_options = true } = {}) =>
    api.get("/catalog/public", { params: { include_options } }).then((r) => r.data),
};

// ====== Admin RDV agenda ======
export const adminRdvApi = {
  list: () => api.get("/admin/rdv").then((r) => r.data),
  create: (body) => api.post("/admin/rdv", body).then((r) => r.data),
  update: (id, body) => api.put(`/admin/rdv/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/admin/rdv/${id}`).then((r) => r.data),
  remind: (id) => api.post(`/admin/rdv/${id}/remind`).then((r) => r.data),
};

// ====== Admin quote requests (from public contact form) ======
export const adminQuotesApi = {
  list: (status) => api.get("/admin/quotes", { params: status ? { status } : {} }).then((r) => r.data),
  get: (id) => api.get(`/admin/quotes/${id}`).then((r) => r.data),
  updateStatus: (id, body) => api.patch(`/admin/quotes/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/admin/quotes/${id}`).then((r) => r.data),
  convertToDevis: (id) => api.post(`/admin/billing/from-quote/${id}`).then((r) => r.data),
};

// ====== Photobooth Gallery ======
export const photoboothGalleryApi = {
  // Client (auth)
  myGallery: () => api.get("/me/photobooth/gallery").then((r) => r.data),
  myFileUrl: (id) => `${api.defaults.baseURL}/me/photobooth/gallery/${id}/file`,
  myZipUrl: () => `${api.defaults.baseURL}/me/photobooth/gallery/zip`,
  // Admin
  adminList: (userId) => api.get("/admin/photobooth/photos", { params: userId ? { user_id: userId } : {} }).then((r) => r.data),
  adminFileUrl: (id) => `${api.defaults.baseURL}/admin/photobooth/photos/${id}/file`,
  adminDelete: (id) => api.delete(`/admin/photobooth/photos/${id}`).then((r) => r.data),
  adminUpload: async ({ user_id, event_label, file }) => {
    const fd = new FormData();
    fd.append("user_id", user_id);
    fd.append("event_label", event_label || "");
    fd.append("file", file);
    const { data } = await api.post("/admin/photobooth/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  // Share codes (admin)
  adminGenerateCode: (payload) => api.post("/admin/photobooth/gallery/generate-code", payload).then((r) => r.data),
  adminListShares: (userId) => api.get("/admin/photobooth/gallery/shares", { params: userId ? { user_id: userId } : {} }).then((r) => r.data),
  adminRevokeShare: (code) => api.post(`/admin/photobooth/gallery/shares/${code}/revoke`).then((r) => r.data),
  // Public (code-based, no auth)
  publicGet: (code) => api.get(`/gallery/${code}`).then((r) => r.data),
  publicFileUrl: (code, photoId) => `${api.defaults.baseURL}/gallery/${code}/photos/${photoId}/file`,
  publicZipUrl: (code) => `${api.defaults.baseURL}/gallery/${code}/zip`,
};

// ====== Public calendar ======
export const publicApi = {
  nextEvent: () => api.get("/events/next-public").then((r) => r.data),
  unavailableDates: () => api.get("/calendar/unavailable").then((r) => r.data),
};
